import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import UpgradeModal from './UpgradeModal';
import { useSubscription, FEATURES } from '../hooks/useSubscription';

/**
 * Feature Gate Context - Provides feature access info across the app
 */
const FeatureGateContext = React.createContext(null);

export function FeatureGateProvider({ children }) {
  const subscriptionData = useSubscription();
  const [upgradeModal, setUpgradeModal] = React.useState({
    isOpen: false,
    feature: null,
    requiredTier: null
  });

  const showUpgradeModal = React.useCallback((feature, requiredTier) => {
    setUpgradeModal({ isOpen: true, feature, requiredTier });
  }, []);

  const closeUpgradeModal = React.useCallback(() => {
    setUpgradeModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  const value = {
    ...subscriptionData,
    showUpgradeModal,
    closeUpgradeModal,
    refreshSubscription: subscriptionData.refresh
  };

  return (
    <FeatureGateContext.Provider value={value}>
      {children}
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={closeUpgradeModal}
        feature={upgradeModal.feature}
        requiredTier={upgradeModal.requiredTier}
      />
    </FeatureGateContext.Provider>
  );
}

export function useFeatureGate() {
  const context = React.useContext(FeatureGateContext);
  if (!context) {
    throw new Error('useFeatureGate must be used within a FeatureGateProvider');
  }
  return context;
}

/**
 * GatedFeature - Wraps any content with feature gating
 * Shows content normally if user has access, or greyed out with lock if not
 */
export function GatedFeature({ 
  feature, 
  children, 
  fallback = null,
  showLock = true,
  className = ''
}) {
  const { hasFeature, getRequiredTier, showUpgradeModal, loading } = useFeatureGate();

  if (loading) return fallback || children;

  const hasAccess = hasFeature(feature);
  const requiredTier = getRequiredTier(feature);

  if (hasAccess) {
    return children;
  }

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    showUpgradeModal(feature, requiredTier);
  };

  return (
    <div 
      className={`relative cursor-pointer ${className}`}
      onClick={handleClick}
      data-testid={`gated-feature-${feature}`}
    >
      <div className="pointer-events-none">
        {children}
      </div>
      {showLock && (
        <div className="absolute top-2 right-2">
          <div className="bg-gray-800/90 rounded-full p-1.5 shadow-lg">
            <Lock className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * GatedButton - A button that shows locked state for gated features
 */
export function GatedButton({
  feature,
  onClick,
  children,
  className = '',
  lockedClassName = '',
  ...props
}) {
  const { hasFeature, getRequiredTier, showUpgradeModal, loading } = useFeatureGate();

  if (loading) return <button className={className} {...props}>{children}</button>;

  const hasAccess = hasFeature(feature);
  const requiredTier = getRequiredTier(feature);

  const handleClick = (e) => {
    if (!hasAccess) {
      e.preventDefault();
      showUpgradeModal(feature, requiredTier);
    } else if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={className}
      data-testid={`gated-btn-${feature}`}
      {...props}
    >
      <span className="flex items-center gap-2">
        {children}
        {!hasAccess && <Lock className="w-4 h-4" />}
      </span>
    </button>
  );
}

/**
 * GatedLink - A Link that shows locked state for gated features
 */
export function GatedLink({
  feature,
  to,
  children,
  className = '',
  activeClassName = '',
  showLockIcon = true,
  ...props
}) {
  const { hasFeature, getRequiredTier, showUpgradeModal, loading } = useFeatureGate();

  if (loading) {
    return <Link to={to} className={className} {...props}>{children}</Link>;
  }

  const hasAccess = hasFeature(feature);
  const requiredTier = getRequiredTier(feature);

  const handleClick = (e) => {
    if (!hasAccess) {
      e.preventDefault();
      showUpgradeModal(feature, requiredTier);
    }
  };

  return (
    <Link
      to={hasAccess ? to : '#'}
      onClick={handleClick}
      className={`${className} ${hasAccess ? activeClassName : ''}`}
      data-testid={`gated-link-${feature}`}
      {...props}
    >
      <span className="flex items-center gap-2">
        {children}
        {!hasAccess && showLockIcon && <Lock className="w-3.5 h-3.5" />}
      </span>
    </Link>
  );
}

/**
 * GatedTab - A tab button with feature gating support
 */
export function GatedTab({
  feature,
  icon,
  label,
  active,
  onClick,
  dataTestId,
  color = 'blue'
}) {
  const { hasFeature, getRequiredTier, showUpgradeModal, loading } = useFeatureGate();

  const hasAccess = !feature || hasFeature(feature);
  const requiredTier = feature ? getRequiredTier(feature) : null;

  const colorClasses = {
    blue: 'text-blue-600',
    amber: 'text-amber-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    emerald: 'text-emerald-600',
    rose: 'text-rose-600',
    indigo: 'text-indigo-600',
    pink: 'text-pink-600',
    cyan: 'text-cyan-600',
  };
  
  const borderClasses = {
    blue: 'border-blue-600',
    amber: 'border-amber-600',
    green: 'border-green-600',
    purple: 'border-purple-600',
    emerald: 'border-emerald-600',
    rose: 'border-rose-600',
    indigo: 'border-indigo-600',
    pink: 'border-pink-600',
    cyan: 'border-cyan-600',
  };

  const handleClick = () => {
    if (!hasAccess) {
      showUpgradeModal(feature, requiredTier);
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`group relative flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-3 sm:py-4 border-b-2 font-medium transition text-xs sm:text-sm ${colorClasses[color]} ${
        active
          ? borderClasses[color]
          : 'border-transparent hover:opacity-80'
      }`}
      data-testid={dataTestId}
      title={hasAccess ? label : `${label} (Upgrade to unlock)`}
    >
      {icon}
      <span>{label}</span>
      {!hasAccess && <Lock className="w-3.5 h-3.5 ml-1" />}
    </button>
  );
}

/**
 * FeatureLockedOverlay - Full overlay for locked sections
 */
export function FeatureLockedOverlay({ feature, children, message }) {
  const { hasFeature, getRequiredTier, showUpgradeModal, loading } = useFeatureGate();

  if (loading) return children;

  const hasAccess = hasFeature(feature);
  const requiredTier = getRequiredTier(feature);

  if (hasAccess) return children;

  const tierNames = {
    seedling: 'Seedling',
    growing: 'Growing',
    flourishing: 'Flourishing'
  };

  return (
    <div className="relative">
      <div className="pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px]">
        <div className="text-center p-6 bg-white rounded-xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Unlock This Feature
          </h3>
          <p className="text-gray-600 mb-4">
            {message || `This feature is available with the ${tierNames[requiredTier] || requiredTier} plan.`}
          </p>
          <button
            onClick={() => showUpgradeModal(feature, requiredTier)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
}

export { FEATURES };
